import React, { useMemo, useCallback } from 'react';
import { View, Platform } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import Squircle from 'design-system/Squircle';
import useColors from 'hooks/useColors';
import { AppView } from 'design-system';
import { OnboardingFlow } from 'utils/types/navigation';
import StatusBar from 'design-system/StatusBar';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useAppSelector } from 'store/hooks';
import { selectFeatureFlag } from 'reducers/selectors';
import { showContinueInMobileAlert } from 'features/pots/utils';
import { assertUnreachable } from 'utils/types';
import { showUnavailableOnWeb } from 'utils/webGate';
import IconUnderReview from '../icons/IconUnderReview';
import IconWarning from '../icons/IconWarning';
import ContactSupportTextButton from './ContactSupportTextButton';
import IconProhibition from '../icons/IconProhibition';
import { isDesktop } from '../../../constants';
const strings = {
    title: {
        NOT_STARTED: 'Under review', // never gets here
        KYC_PENDING: 'Under review',
        IDENTITY_CHECKS_IN_PROGRESS: 'Under review',
        IDENTITY_CHECKS_NEED_USER_ACTION: 'Action required',
        NOT_ELIGIBLE: 'Failed verification',
        OPENING_IN_PROGRESS: 'Under review',
        ACTIVE: 'Under review',
        CLOSING_IN_PROGRESS: 'Account will be closed',
        CLOSED: 'Account Closed',
        FROZEN: 'Account Frozen',
    },
    subtitle: {
        [OnboardingFlow.Invest]: {
            NOT_STARTED: '',
            KYC_PENDING: 'We will let you know once it’s approved.',
            IDENTITY_CHECKS_IN_PROGRESS: 'We will let you know once it’s approved. In\xa0the meantime, discover\xa0your next\xa0great\xa0trade.',
            IDENTITY_CHECKS_NEED_USER_ACTION: 'We couldn’t verify your account. Please Review and provide additional details.',
            NOT_ELIGIBLE: 'We have unfortunately failed to verify your details with the documents you have provided. Get in touch with customer support, so they can help you complete onboarding.',
            OPENING_IN_PROGRESS: 'We will let you know once it’s approved. In\xa0the meantime, discover\xa0your next\xa0great\xa0trade.',
            ACTIVE: '',
            CLOSING_IN_PROGRESS: 'We are closing your account.',
            CLOSED: 'Your Investment account has been closed.',
            FROZEN: 'Your Investment account is temporarily frozen.',
        },
        [OnboardingFlow.Pots]: {
            NOT_STARTED: '',
            KYC_PENDING: 'We will let you know once it’s approved.',
            IDENTITY_CHECKS_IN_PROGRESS: 'We will let you know once it’s approved.',
            IDENTITY_CHECKS_NEED_USER_ACTION: 'We couldn’t verify your account. Please Review and provide additional details.',
            NOT_ELIGIBLE: 'We have unfortunately failed to verify your details with the documents you have provided. Get in touch with customer support, so they can help you complete onboarding.',
            OPENING_IN_PROGRESS: 'We will let you know once it’s approved.',
            ACTIVE: '',
            CLOSING_IN_PROGRESS: 'We are closing your account.',
            CLOSED: 'Your account has been closed.',
            FROZEN: 'Your account is temporarily frozen.',
        },
        [OnboardingFlow.Wallet]: {
            NOT_STARTED: '',
            KYC_PENDING: 'We will let you know once it’s approved.',
            IDENTITY_CHECKS_IN_PROGRESS: 'We will let you know once it’s approved.',
            IDENTITY_CHECKS_NEED_USER_ACTION: 'We couldn’t verify your account. Please Review and provide additional details.',
            NOT_ELIGIBLE: 'We have unfortunately failed to verify your details with the documents you have provided. Get in touch with customer support, so they can help you complete onboarding.',
            OPENING_IN_PROGRESS: 'We will let you know once it’s approved.',
            ACTIVE: '',
            CLOSING_IN_PROGRESS: 'We are closing your account.',
            CLOSED: 'Your account has been closed.',
            FROZEN: 'Your account is temporarily frozen.',
        },
    },
    subtitleRequiredUserAction: {
        UPLOAD_IDENTITY_DOCUMENT: 'We need to verify your details for regulatory purposes. Tap Review to provide both proofs of your identity and address.',
        UPLOAD_PROOF_OF_ADDRESS: 'We need to verify your address.\n\nTap Review to provide proof of your address.',
    },
    button: 'Search for stocks',
    review: 'Review',
    buttonRequiredUserAction: {
        UPLOAD_IDENTITY_DOCUMENT: 'Review',
        UPLOAD_PROOF_OF_ADDRESS: 'Review',
    },
};
const getTitle = (status, requiredUserAction, rejectionReasonString) => {
    if (status === 'KYC_PENDING') {
        if (requiredUserAction === 'UPLOAD_IDENTITY_DOCUMENT' || requiredUserAction === 'UPLOAD_PROOF_OF_ADDRESS') {
            return strings.title.IDENTITY_CHECKS_NEED_USER_ACTION + (rejectionReasonString ? ': Failed verification' : '');
        }
    }
    return strings.title[status];
};
// Identity checks stay in the mobile app. The QR code opens the flow the customer started from.
const VERIFY_ON_WEB_TITLE = 'Verify your identity';
const getVerifyAppLink = (flow) => flow === OnboardingFlow.Pots ? 'emma://save?tab=pots' : 'emma://invest/account';
const UnderReview = ({ flow = OnboardingFlow.Invest, status, requiredUserAction, rejectionReasonString, }) => {
    const { navigateDeprecated } = useMainStackNavigation();
    const colors = useColors();
    const kycErrorMessage = useAppSelector((state) => selectFeatureFlag(state, 'kyc_error_message'));
    const onIdentityVerification = useCallback(() => {
        if (showUnavailableOnWeb(getVerifyAppLink(flow), VERIFY_ON_WEB_TITLE))
            return;
        if (isDesktop) {
            showContinueInMobileAlert();
            return;
        }
        navigateDeprecated('WealthDocumentUploadIntroScreen', {
            documentType: 'POI',
            flow,
        });
    }, [flow]);
    const onProofOfAddressVerification = useCallback(() => {
        if (showUnavailableOnWeb(getVerifyAppLink(flow), VERIFY_ON_WEB_TITLE))
            return;
        if (isDesktop) {
            showContinueInMobileAlert();
            return;
        }
        navigateDeprecated('WealthDocumentUploadIntroScreen', {
            flow,
            documentType: 'POA',
        });
    }, [flow]);
    const styles = useStyles(styleSet);
    const [icon, subtitle, onPress, buttonText] = useMemo(() => {
        let i = <IconUnderReview />;
        let s = strings.subtitle[flow]?.[status];
        let p;
        let bt;
        switch (status) {
            case 'NOT_STARTED':
                // this component gets never rendered for this status
                break;
            case 'IDENTITY_CHECKS_IN_PROGRESS':
            case 'OPENING_IN_PROGRESS':
                // default state above - "In review"
                break;
            case 'NOT_ELIGIBLE':
                i = <IconProhibition />;
                if (kycErrorMessage?.extra?.message) {
                    s = kycErrorMessage?.extra?.message;
                }
                break;
            case 'CLOSING_IN_PROGRESS':
            case 'CLOSED':
            case 'FROZEN':
                // red badge but can go to search
                i = <IconWarning />;
                break;
            case 'KYC_PENDING':
            case 'IDENTITY_CHECKS_NEED_USER_ACTION':
                switch (requiredUserAction) {
                    case 'UPLOAD_IDENTITY_DOCUMENT':
                        // red badge but with explanation and corresponding onPress action
                        i = <IconWarning />;
                        p = onIdentityVerification;
                        s = strings.subtitleRequiredUserAction[requiredUserAction];
                        bt = strings.buttonRequiredUserAction[requiredUserAction];
                        break;
                    case 'UPLOAD_PROOF_OF_ADDRESS':
                        // red badge but with explanation and corresponding onPress action
                        i = <IconWarning />;
                        p = onProofOfAddressVerification;
                        s = strings.subtitleRequiredUserAction[requiredUserAction];
                        bt = strings.buttonRequiredUserAction[requiredUserAction];
                        break;
                    default:
                    // Gets here when the status is 'KYC_PENDING', but `requiredUserAction` is not set.
                }
                if (rejectionReasonString) {
                    s = rejectionReasonString;
                    bt = 'Reverify';
                }
                break;
            case 'ACTIVE':
                // this component gets never rendered for this status
                break;
            default:
                // all statuses should be handled above. If new status is added - typesciprt will warn us
                assertUnreachable(status);
        }
        return [i, s, p, bt];
    }, [
        flow,
        status,
        kycErrorMessage?.extra?.message,
        requiredUserAction,
        rejectionReasonString,
        onIdentityVerification,
        onProofOfAddressVerification,
    ]);
    return (<AppView>
      <StatusBar animated barStyle="dark-content"/>
      <View style={styles.underReviewContainer}>
        <View style={styles.container}>
          <Squircle width={rem(100)} height={rem(100)} color={colors.cards.onDark} svgStyle={styles.iconContainer}>
            {icon}
          </Squircle>
          <Spacer h={64}/>
          <Text Text-24 centered>
            {getTitle(status, requiredUserAction)}
          </Text>
          <Spacer h={8}/>
          <Text TextThin-14 Secondary centered>
            {subtitle}
          </Text>
          <Spacer h={24}/>
          <ContactSupportTextButton />
          <Spacer h={24}/>
          {!!buttonText && !!onPress && (<TouchableBounce onPress={onPress}>
              <View style={styles.searchButton}>
                <Text Text-16 centered OnBrand numberOfLines={1} adjustsFontSizeToFit>
                  {buttonText}
                </Text>
              </View>
            </TouchableBounce>)}
        </View>
      </View>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        alignItems: 'center',
        paddingTop: rem(16),
        paddingBottom: rem(24),
        paddingHorizontal: rem(24),
    },
    searchButton: {
        flexDirection: 'row',
        justifyContent: 'center',
        paddingHorizontal: rem(62),
        paddingVertical: rem(16),
        borderRadius: rem(50),
        backgroundColor: colors.background.brandLight,
    },
    underReviewContainer: {
        flex: 1,
        justifyContent: 'center',
    },
    iconContainer: {
        ...Platform.select({
            android: {
                width: rem(60),
                height: rem(60),
                borderRadius: rem(60),
            },
        }),
        ...colors.shadows.statusIconContainer,
    },
}));
export default UnderReview;
